/**
 * StructuredEditorErrorBoundary
 *
 * Catches runtime errors in the structured care plan editor sub-tree so a
 * single bad JSX/state reference cannot white-screen the entire Care Plans
 * tab. The fallback UI is intentionally minimal:
 *   - explains what happened in plain language
 *   - offers a "Retry" reset and a "Return to Care Plans list" action
 *   - never echoes the participant id, plan id, or any structured row
 *     contents (we only log a developer-oriented signature to the console)
 *
 * Class component is required by the React error boundary contract
 * (`getDerivedStateFromError` + `componentDidCatch`); the rest of the
 * structured matrix is hooks-based.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertOctagon, RotateCcw, ArrowLeft } from 'lucide-react';

export class StructuredEditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorSignature: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    // Only keep a non-sensitive signature (error name + first line of message)
    const name = (error && error.name) || 'Error';
    const message = ((error && error.message) || '').split('\n')[0].slice(0, 160);
    return { hasError: true, errorSignature: `${name}: ${message}` };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[StructuredEditorErrorBoundary] caught:', error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState((s) => ({ hasError: false, errorSignature: null, resetKey: s.resetKey + 1 }));
  };

  handleClose = () => {
    this.setState({ hasError: false, errorSignature: null });
    if (typeof this.props.onClose === 'function') this.props.onClose();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card data-testid="structured-editor-error-boundary">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start gap-3">
              <AlertOctagon className="h-5 w-5 text-rose-600 mt-0.5" />
              <div className="space-y-1">
                <h3 className="font-semibold text-sm">Structured care plan editor hit an unexpected error</h3>
                <p className="text-xs text-muted-foreground">
                  The matrix view has been paused so the rest of the Care Plans tab stays usable.
                  Your unsaved edits in this view (if any) were not transmitted to the server.
                </p>
                <p className="text-[11px] font-mono text-muted-foreground mt-2" data-testid="structured-editor-error-signature">
                  {this.state.errorSignature || 'Unknown runtime error'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={this.handleRetry} data-testid="structured-editor-error-retry">
                <RotateCcw className="h-3 w-3 mr-1" /> Retry
              </Button>
              <Button size="sm" variant="ghost" onClick={this.handleClose} data-testid="structured-editor-error-back">
                <ArrowLeft className="h-3 w-3 mr-1" /> Return to Care Plans list
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
    // Force a fresh sub-tree on retry by keying the children with resetKey.
    return (
      <React.Fragment key={this.state.resetKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default StructuredEditorErrorBoundary;
